import { useQuery } from "@tanstack/react-query";
import type { SizeDownloadManifest } from "@shared/iteration-size-downloads";
import { IterationSizeDownloadControls } from "./iteration-size-download-controls";
import type { IterationSizeController } from "@/lib/iteration-size-downloads";
import type { ReactNode } from "react";
import { IterationSizeSaveControls } from "./iteration-size-save-controls";

/** Mounted only for enabled Creative Iterations image results. */
export function IterationSizesActions({ setId, resultId, controller, action = "download", alreadySaved = false, onSaveCurrent, fallback }: {
  setId: string; resultId?: string; controller: IterationSizeController;
  action?: "download" | "save"; alreadySaved?: boolean; onSaveCurrent?: () => Promise<boolean>; fallback?: ReactNode;
}) {
  const manifest = useQuery<SizeDownloadManifest>({
    queryKey: [`/api/creative-iterations/sets/${setId}/size-downloads`],
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const response = await fetch(`/api/creative-iterations/sets/${setId}/size-downloads`, { credentials: "include" });
      if (!response.ok) throw new Error("Could not verify eligible sizes");
      return response.json();
    },
  });
  if (!manifest.data && action === "save") return <>{fallback}</>;
  if (!manifest.data) return !resultId && <span role="status" className="self-center text-xs text-muted-foreground">
    {manifest.isError ? <button type="button" className="underline" onClick={() => void manifest.refetch()}>Retry size preparation</button> : "Checking eligible sizes…"}
  </span>;
  const items = resultId ? manifest.data.items.filter(item => item.resultId === resultId) : manifest.data.items;
  if (!items.length && action === "save") return <>{fallback}</>;
  if (!items.length) return !resultId && <div className="text-xs text-muted-foreground" role="status">
    No verified images eligible for other-size downloads.
    <ul>{manifest.data.excluded.map((item, index) => <li key={index}>{item.name}: {item.reason}</li>)}</ul>
  </div>;
  if (action === "save" && onSaveCurrent) return <span className="inline-flex min-w-0 flex-1" onClick={event => event.stopPropagation()}>
    <IterationSizeSaveControls setId={setId} item={items[0]} controller={controller} alreadySaved={alreadySaved} onSaveCurrent={onSaveCurrent} />
  </span>;
  return <span onClick={event => event.stopPropagation()}>
    <IterationSizeDownloadControls setId={setId} items={items} excluded={resultId ? [] : manifest.data.excluded} tile={!!resultId} controller={controller} />
  </span>;
}
